import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { SwapLabel } from '@/components/generals/swap-label'

type Variant = 'solid' | 'outline' | 'frosted' | 'ghost'
type Size = 'sm' | 'md'

type ActionButtonProps = {
    href: string
    children: ReactNode
    variant?: Variant
    size?: Size
    className?: string
    block?: boolean
    onClick?: () => void
}

const base = 'fw-swap-host group relative inline-flex items-center justify-center overflow-hidden text-center font-sans text-[14px] font-medium uppercase leading-[1em] tracking-[0.02em] transition-colors duration-300'

const variants: Record<Variant, string> = {
    solid: 'bg-[#fefff5] text-[#0d0d0d] hover:bg-white',
    outline: 'border border-[#454545] text-ink hover:border-[#fefff5] hover:bg-[#ffffff08]',
    frosted: 'bg-[#ffffff2b] text-ink backdrop-blur-[2px] hover:bg-[#ffffff45]',
    ghost: 'text-ink hover:text-white'
}

const sizes: Record<Size, string> = {
    sm: 'h-[38px] px-4',
    md: 'h-[46px] px-5'
}

const isExternal = (href: string): boolean => href.startsWith('http') || href.startsWith('mailto:')

export const ActionButton: FC<ActionButtonProps> = ({
    href,
    children,
    variant = 'outline',
    size = 'md',
    className,
    block = false,
    onClick
}): ReactNode => {
    const classes = cn(base, variants[variant], sizes[size], block && 'w-full', className)

    if (isExternal(href)) {
        return (
            <a
                href={href}
                target='_blank'
                rel='noreferrer noopener'
                className={classes}
                onClick={onClick}
            >
                <SwapLabel>
                    {children}
                </SwapLabel>
            </a>
        )
    }

    return (
        <Link href={href} className={classes} onClick={onClick}>
            <SwapLabel>
                {children}
            </SwapLabel>
        </Link>
    )
}
